/* eslint-disable react/require-default-props */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { pdfjs, Document, Page } from 'react-pdf';
import useDebounceFn from '@hooks/useDebounceFn';
import { Button } from '../../atoms/Button/Button';
import './PDFViewerStyles.scss';
import 'react-pdf/dist/esm/Page/TextLayer.css';
// react-pdf runs its parser in a web worker. Kept on the CDN to match the legacy
// viewer's proven behaviour; bundling it locally would remove the external
// dependency but is a build change, so it is tracked separately.
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
const ZOOM_STEP = 0.5;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;
const DEFAULT_ZOOM = 1;
const WIDTH_CHANGE_THRESHOLD = 5;
const THUMBNAIL_HEIGHT = 93;
/**
 * PDFViewer
 *
 * Quimby document viewer. Renders every page in a scrolling column with zoom
 * controls, and optionally a thumbnail rail for jumping between pages.
 *
 * Sizes pages to its container by default, so it shrinks with the space it is
 * given rather than forcing its parent to grow.
 */
function PDFViewerBase({ file, pageWidth, pageHeight, defaultZoom, showThumbnails = false, ariaLabel, variant = 'full', className = '', onStartSelecting, onStopSelecting }) {
    const [numPages, setNumPages] = useState(0);
    const [selectedPage, setSelectedPage] = useState(0);
    const [autoPageWidth, setAutoPageWidth] = useState(0);
    const [zoomLevel, setZoomLevel] = useState(defaultZoom !== null && defaultZoom !== void 0 ? defaultZoom : DEFAULT_ZOOM);
    const pageRefs = useRef([]);
    const measuredWidth = useRef(0);
    const documentRef = useRef(null);
    const debouncedSetWidth = useDebounceFn(setAutoPageWidth, 300);
    const observer = useMemo(() => new ResizeObserver((entries) => {
        const [entry] = entries;
        const { width } = entry.contentRect;
        if (Math.abs(width - measuredWidth.current) <= WIDTH_CHANGE_THRESHOLD)
            return;
        debouncedSetWidth(width);
    }), [debouncedSetWidth]);
    useEffect(() => {
        setZoomLevel(defaultZoom !== null && defaultZoom !== void 0 ? defaultZoom : DEFAULT_ZOOM);
    }, [defaultZoom]);
    useEffect(() => {
        measuredWidth.current = autoPageWidth;
    }, [autoPageWidth]);
    useEffect(() => {
        if (!file)
            observer.disconnect();
        return () => observer.disconnect();
    }, [file, observer]);
    const onDocumentLoadSuccess = ({ numPages: pageCount }) => {
        // Build the refs before the render that consumes them. Doing this in an effect
        // keyed on numPages runs one render too late, so every <Page> would mount with
        // `inputRef={undefined}` and jumpToPage would have nothing to scroll to.
        pageRefs.current = Array.from({ length: pageCount }, () => React.createRef());
        setNumPages(pageCount);
        if (pageCount === 0)
            return;
        setTimeout(() => {
            var _a, _b, _c;
            observer.disconnect();
            const element = documentRef.current;
            const firstPage = (_c = (_b = (_a = element === null || element === void 0 ? void 0 : element.pages) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.parentElement;
            if (firstPage)
                observer.observe(firstPage);
        }, 300);
    };
    const checkSelection = useCallback(() => {
        var _a, _b;
        if (!onStartSelecting && !onStopSelecting)
            return;
        const element = documentRef.current;
        const selection = window.getSelection();
        if ((selection === null || selection === void 0 ? void 0 : selection.toString().trim()) && selection.anchorNode) {
            const page = (_b = (_a = element === null || element === void 0 ? void 0 : element.pages) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.findIndex((pageEl) => pageEl === null || pageEl === void 0 ? void 0 : pageEl.contains(selection.anchorNode));
            if (page !== undefined && page >= 0) {
                onStartSelecting === null || onStartSelecting === void 0 ? void 0 : onStartSelecting({ page: page + 1, text: selection.toString() });
                return;
            }
        }
        onStopSelecting === null || onStopSelecting === void 0 ? void 0 : onStopSelecting();
    }, [onStartSelecting, onStopSelecting]);
    const jumpToPage = (index) => {
        var _a, _b;
        setSelectedPage(index);
        (_b = (_a = pageRefs.current[index]) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.scrollIntoView();
    };
    const zoomIn = () => setZoomLevel((z) => Math.min(z + ZOOM_STEP, MAX_ZOOM));
    const zoomOut = () => setZoomLevel((z) => Math.max(z - ZOOM_STEP, MIN_ZOOM));
    const classes = ['qmb-ui-pdf-viewer', showThumbnails ? 'qmb-ui-pdf-viewer--with-thumbnails' : '', className]
        .filter(Boolean)
        .join(' ');
    // The label goes on a wrapper, not on `Document`. react-pdf passes unknown props
    // through `makeEventProps`, which builds a fresh object from a fixed list of React
    // event names — anything that is not an event, `aria-label` included, is dropped
    // before its div renders. Passing it to `Document` produced no attribute at all.
    //
    // A `region` with no accessible name is noise to a screen reader, so the role is
    // only applied when there is a label to go with it.
    // `simple` hands the document to the browser's own PDF plugin, which needs a URL. `PDFFile`
    // also accepts a Blob, an ArrayBuffer and a `{ data }` source, so those are wrapped in an
    // object URL — otherwise the variant rendered an empty frame for inputs its own type allows.
    const simpleObjectUrl = useMemo(() => {
        if (variant !== 'simple' || file == null || typeof file === 'string')
            return null;
        if (typeof file === 'object' && 'url' in file)
            return null;
        let data;
        if (file instanceof Blob)
            data = file;
        else if ('data' in file)
            data = file.data;
        else
            data = file;
        if (data == null)
            return null;
        const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
        return URL.createObjectURL(blob);
    }, [variant, file]);
    useEffect(() => () => {
        if (simpleObjectUrl)
            URL.revokeObjectURL(simpleObjectUrl);
    }, [simpleObjectUrl]);
    if (variant === 'simple') {
        let href;
        if (typeof file === 'string')
            href = file;
        else if (file != null && typeof file === 'object' && 'url' in file)
            href = file.url;
        else
            href = simpleObjectUrl !== null && simpleObjectUrl !== void 0 ? simpleObjectUrl : undefined;
        return (React.createElement("div", { className: ['qmb-ui-pdf-viewer', 'qmb-ui-pdf-viewer--simple', className].filter(Boolean).join(' '), role: ariaLabel ? 'region' : undefined, "aria-label": ariaLabel }, href && (React.createElement("object", { data: href, type: "application/pdf", className: "qmb-ui-pdf-viewer__object" },
            React.createElement("p", null,
                React.createElement("a", { href: href, target: "_blank", rel: "noopener noreferrer" }, "Download PDF"))))));
    }
    return (React.createElement("div", { className: "qmb-ui-pdf-viewer__region", role: ariaLabel ? 'region' : undefined, "aria-label": ariaLabel },
        React.createElement(Document, { ref: documentRef, file: file, onLoadSuccess: onDocumentLoadSuccess, onMouseUp: () => {
                setTimeout(checkSelection, 200);
            }, className: classes },
            showThumbnails && (React.createElement("nav", { className: "qmb-ui-pdf-viewer__thumbnails", "aria-label": "Pages" }, Array.from({ length: numPages }, (_, index) => (React.createElement("button", { type: "button", key: index, className: "qmb-ui-pdf-viewer__thumbnail", "aria-label": `Page ${index + 1}`, "aria-current": index === selectedPage, onClick: () => jumpToPage(index) },
                React.createElement(Page, { className: `qmb-ui-pdf-viewer__page ${index === selectedPage ? 'is-selected' : ''}`, pageNumber: index + 1, height: THUMBNAIL_HEIGHT, renderAnnotationLayer: false, renderTextLayer: false }),
                React.createElement("span", { className: "qmb-ui-pdf-viewer__page-index" }, index + 1)))))),
            React.createElement("div", { className: "qmb-ui-pdf-viewer__pages" },
                Array.from({ length: numPages }, (_, index) => (React.createElement(Page, { inputRef: pageRefs.current[index], key: index, className: "qmb-ui-pdf-viewer__page", pageNumber: index + 1, width: pageWidth !== null && pageWidth !== void 0 ? pageWidth : (autoPageWidth ? Math.floor(autoPageWidth) : undefined), height: pageHeight, scale: zoomLevel, renderAnnotationLayer: false, renderTextLayer: true }))),
                React.createElement("div", { className: "qmb-ui-pdf-viewer__zoom" },
                    React.createElement(Button, { "aria-label": "Zoom in", onClick: zoomIn, disabled: zoomLevel >= MAX_ZOOM },
                        React.createElement("i", { className: "fa-light fa-magnifying-glass-plus", "aria-hidden": "true" })),
                    React.createElement("i", { className: "fa-light fa-grid-line-vertical", "aria-hidden": "true" }),
                    React.createElement(Button, { "aria-label": "Zoom out", onClick: zoomOut, disabled: zoomLevel <= MIN_ZOOM },
                        React.createElement("i", { className: "fa-light fa-magnifying-glass-minus", "aria-hidden": "true" })))))));
}
/**
 * Memoised so a parent re-render does not tear down and re-parse the document.
 * Exported memoised rather than raw so every consumer gets that for free.
 */
export const PDFViewer = React.memo(PDFViewerBase);
